import { XIcon, QrCodeIcon, ScanLineIcon } from 'lucide-react-native';
import * as React from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  useWindowDimensions,
  View,
} from 'react-native';

import { androidStatusBarHeight, ModalStatusBarBand } from '@/components/android-status-bar-band';
import { CheckedInConfirmation } from '@/components/events/checked-in-confirmation';
import { ClubCheckInContent } from '@/components/events/club-check-in-content';
import { MemberIdentityQr } from '@/components/qr/member-identity-qr';
import { MemberScanFlow } from '@/components/qr/member-scan-flow';
import { useSession } from '@/components/session-provider';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { fetchMyLiveCheckIn } from '@/lib/event-checkins';
import { fetchMemberQrJwt } from '@/lib/member-qr-client';
import { BRAND } from '@sixam/config/brand';

// Fast enough that the runner sees it while still standing in front of the
// operator, matching the event check-in screen.
const CHECK_IN_POLL_MS = 3000;

/**
 * The member's own QR: show it so an operator can scan you in, or scan someone
 * else. This lives on both the personal profile and the club profile because it
 * is member identity, not scope-specific — an admin still wants to show their
 * own code and scan runners while viewing the club.
 */
type MemberQrControlsProps = Readonly<{
  /**
   * When set, the scan button runs event check-in for this club (the admin
   * "check people into an event" flow) instead of the friend-connect scan.
   */
  checkInTenantId?: string | null;
  /** Tenant slug the scanner acts within; scanning is hidden without one. */
  tenantSlug?: string | null;
  /**
   * Distance from the top of the screen for the floating buttons, so each screen
   * can place them clear of its own header.
   */
  topOffset: number;
  /**
   * Open flags are controlled by the parent so the platform's own header
   * controls (the iOS toolbar) and the Android floating buttons can both drive
   * the same modals.
   */
  qrOpen: boolean;
  scannerOpen: boolean;
  onQrOpenChange: (open: boolean) => void;
  onScannerOpenChange: (open: boolean) => void;
  scannerEnabled?: boolean;
}>;

// The shared control coordinates member QR, friend scanning, and operator check-in variants.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function MemberQrControls({
  checkInTenantId,
  tenantSlug,
  topOffset,
  qrOpen: isQrOpen,
  scannerOpen: isScannerOpen,
  onQrOpenChange,
  onScannerOpenChange,
  scannerEnabled = true,
}: MemberQrControlsProps) {
  const { session } = useSession();
  const userId = session?.user.id;
  const { width: windowWidth } = useWindowDimensions();

  const [memberJwt, setMemberJwt] = React.useState<string | null>(null);
  const [memberQrError, setMemberQrError] = React.useState<string | null>(null);
  const [isLoadingMemberQr, setIsLoadingMemberQr] = React.useState(false);
  const [memberQrRequest, setMemberQrRequest] = React.useState(0);
  // Set when an operator scans this code. Walk-ups never registered for anything,
  // so this is the only way they learn the scan worked.
  const [checkedInEvent, setCheckedInEvent] = React.useState<string | null>(null);

  // Fill most of the screen width with a side margin, capped so the code does
  // not become unreadably large on tablets.
  const qrSize = Math.min(windowWidth - 80, 380);

  React.useEffect(() => {
    if (!isQrOpen || !userId) {
      return undefined;
    }

    let active = true;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const abortController = new AbortController();

    // The code never expires, so it is fetched once and left alone. Only a
    // failure schedules a retry.
    async function loadMemberQr() {
      setIsLoadingMemberQr(true);
      setMemberQrError(null);
      try {
        const token = await fetchMemberQrJwt(abortController.signal);
        if (!active) {
          return;
        }
        setMemberJwt(token.jwt);
      } catch (qrError) {
        if (active) {
          setMemberQrError(
            qrError instanceof Error ? qrError.message : 'Could not create member QR code.'
          );
          refreshTimer = setTimeout(() => void loadMemberQr(), 10_000);
        }
      } finally {
        if (active) {
          setIsLoadingMemberQr(false);
        }
      }
    }

    void loadMemberQr();
    return () => {
      active = false;
      abortController.abort();
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [isQrOpen, memberQrRequest, userId]);

  // Watch for the scan while the code is on screen, the same way the event
  // check-in screen does, so a walk-up gets the identical confirmation.
  React.useEffect(() => {
    if (!isQrOpen || !userId || checkedInEvent) {
      return undefined;
    }

    let active = true;

    async function check() {
      try {
        const mine = await fetchMyLiveCheckIn();
        if (active && mine.checkedIn) {
          setCheckedInEvent(mine.event?.title ?? '');
        }
      } catch {
        // A missed poll is not worth reporting; the next one will do.
      }
    }

    void check();
    const timer = setInterval(() => void check(), CHECK_IN_POLL_MS);

    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [checkedInEvent, isQrOpen, userId]);

  function openMemberQr() {
    setMemberJwt(null);
    setMemberQrError(null);
    setCheckedInEvent(null);
    onQrOpenChange(true);
  }

  let memberQrContent: React.ReactNode = (
    <Text variant="muted">Sign in to show your member QR code.</Text>
  );
  if (isLoadingMemberQr && !memberJwt) {
    memberQrContent = <ActivityIndicator />;
  } else if (memberQrError && !memberJwt) {
    memberQrContent = (
      <View className="items-center gap-3">
        <Text className="text-center text-sm text-destructive" selectable>
          {memberQrError}
        </Text>
        <Button onPress={() => setMemberQrRequest((request) => request + 1)}>
          <Text>Try again</Text>
        </Button>
      </View>
    );
  } else if (checkedInEvent !== null) {
    memberQrContent = <CheckedInConfirmation eventTitle={checkedInEvent || null} />;
  } else if (memberJwt) {
    memberQrContent = (
      <View className="rounded-md border-2 border-border bg-white p-3">
        <MemberIdentityQr size={qrSize} value={memberJwt} />
      </View>
    );
  }

  let scannerBody: React.ReactNode;
  if (checkInTenantId) {
    scannerBody = <ClubCheckInContent tenantId={checkInTenantId} />;
  } else {
    // Friend-connect scan, available to any member. Tenant is only needed for the
    // optional "add to event" action, which is not offered here.
    scannerBody = <MemberScanFlow tenantSlug={tenantSlug ?? undefined} />;
  }

  return (
    <>
      {Platform.OS === 'android' ? (
        <View
          className="absolute right-3 flex-row gap-2"
          style={{ elevation: 12, top: topOffset, zIndex: 12 }}
        >
          {scannerEnabled ? (
            <Pressable
              accessibilityLabel={
                checkInTenantId ? 'Check runners into an event' : 'Scan a member QR'
              }
              accessibilityRole="button"
              className="size-11 items-center justify-center rounded-full active:opacity-80"
              hitSlop={4}
              onPress={() => onScannerOpenChange(true)}
              style={{ borderColor: BRAND.color.orange, borderWidth: 2 }}
            >
              <ScanLineIcon color={BRAND.color.orange} size={22} />
            </Pressable>
          ) : null}
          <Pressable
            accessibilityLabel="Show my QR code"
            accessibilityRole="button"
            className="size-11 items-center justify-center rounded-full active:opacity-80"
            hitSlop={4}
            onPress={openMemberQr}
            style={{ backgroundColor: BRAND.color.orange }}
          >
            <QrCodeIcon color={BRAND.color.white} size={22} />
          </Pressable>
        </View>
      ) : null}

      <Modal
        animationType="slide"
        onRequestClose={() => onQrOpenChange(false)}
        presentationStyle="pageSheet"
        statusBarTranslucent
        visible={isQrOpen}
      >
        <View className="flex-1 bg-background" style={{ paddingTop: androidStatusBarHeight() }}>
          <ModalStatusBarBand />
          <View className="flex-row items-center justify-between p-6 pb-0">
            <Text variant="h2">My QR</Text>
            <Button
              accessibilityLabel="Close QR"
              onPress={() => onQrOpenChange(false)}
              size="icon"
              variant="secondary"
            >
              <XIcon color="white" size={18} />
            </Button>
          </View>
          <View className="flex-1 items-center justify-center p-6">{memberQrContent}</View>
        </View>
      </Modal>

      {scannerEnabled ? (
        <Modal
          animationType="slide"
          onRequestClose={() => onScannerOpenChange(false)}
          presentationStyle="pageSheet"
          statusBarTranslucent
          visible={isScannerOpen}
        >
          <View
            className="flex-1 gap-4 bg-background p-6"
            style={{ paddingTop: androidStatusBarHeight() + 24 }}
          >
            <ModalStatusBarBand />
            <View className="flex-row items-center justify-between">
              <Text variant="h2">{checkInTenantId ? 'Event check-in' : 'Scan QR'}</Text>
              <Button
                accessibilityLabel="Close scanner"
                onPress={() => onScannerOpenChange(false)}
                size="icon"
                variant="secondary"
              >
                <XIcon color="white" size={18} />
              </Button>
            </View>
            {scannerBody}
          </View>
        </Modal>
      ) : null}
    </>
  );
}
